import React from 'react';
import MonoAvatar from '../../MonoAvatar';
import {
  InlineShell,
  InlineEyebrow,
  formatRelativeShort,
  type F8CardData,
  type OnAction,
} from './cardHelpers';

/**
 * PPL-450 — the `rooms_list` card. f8's answer to "what rooms do I have open?"
 * and "how's my room with Sam going?".
 *
 * Rooms are the PERSONAL half of pplkpr, so a row names the counterpart by the
 * first name the server put in `otherName` (it already fell back to the
 * inviter's typed invitee name, then to an @handle). The row shows only what
 * the consent chokepoint allows out of the room — state, counterpart,
 * timestamps and the inviter's short reason — never prep, consent flags or the
 * synthesis. Everything finer lives one tap away, behind `RoomDetail`.
 */
interface Room {
  mediationId?: string;
  state?: string;
  stateLabel?: string;
  role?: 'inviter' | 'invitee';
  otherName?: string;
  otherAvatar?: string | null;
  reason?: string;
  lastActivityAt?: any;
}

// A room that is open, or about to be — the only rows that get the live pill.
// Kept byte-identical to the app card's set: the same room must not read as
// live on one surface and as still-waiting on the other.
const LIVE_STATES = new Set(['rules_set', 'joint', 'ongoing']);
// Rooms that are over or on hold read back, not forward.
const MUTED_STATES = new Set(['paused', 'ended']);

function stateClassName(state: string | undefined): string {
  if (state && LIVE_STATES.has(state)) return 'f8c-room-state f8c-room-state-live';
  if (state && MUTED_STATES.has(state)) return 'f8c-room-state f8c-room-state-muted';
  return 'f8c-room-state';
}

function RoomRow({ room, onAction }: { room: Room; onAction?: OnAction }) {
  const name = room.otherName || 'someone';
  const photo = room.otherAvatar;
  const label = room.stateLabel || room.state || '';
  const reason = room.reason || '';
  const when = formatRelativeShort(room.lastActivityAt);

  return (
    <button
      type="button"
      className="f8c-room-row"
      onClick={() =>
        onAction?.({
          action: 'navigate',
          params: { screen: 'RoomDetail', mediationId: room.mediationId },
        })
      }
    >
      {photo ? (
        <img className="f8c-room-avatar" src={photo} alt={name} />
      ) : (
        <MonoAvatar name={name} size={40} />
      )}

      <div className="f8c-room-body">
        <div className="f8c-room-top">
          <span className="f8c-room-name">{name}</span>
          {when && <span className="f8c-room-when">{when}</span>}
        </div>
        {label && <span className={stateClassName(room.state)}>{label}</span>}
        {reason && <div className="f8c-room-reason">{reason}</div>}
      </div>
    </button>
  );
}

export default function RoomsListCard({
  card,
  onAction,
}: {
  card: F8CardData;
  onAction?: OnAction;
}) {
  const rooms: Room[] = card?.data?.rooms || [];

  // The handler never emits this card with zero rows (an empty card would open
  // an empty voice overlay panel), so this branch is defensive — but if one
  // ever arrives, say the card's own sentence rather than vanishing mid-reply.
  if (rooms.length === 0) {
    const fallbackText =
      typeof card?.data?.fallbackText === 'string' ? card.data.fallbackText.trim() : '';
    return (
      <InlineShell>
        <p className="f8c-fallback-text">
          {fallbackText || "you don't have any rooms open right now."}
        </p>
      </InlineShell>
    );
  }

  // `count`/`rooms` are the page the card shows; `total` is how many rooms the
  // user actually has. Saying "5 rooms" to someone with twelve is the same
  // false claim the spoken summary used to make.
  const shown = rooms.length;
  const rawTotal = card?.data?.total;
  const total = typeof rawTotal === 'number' && rawTotal > shown ? rawTotal : shown;
  const eyebrow =
    total > shown ? `${shown} of ${total} rooms` : `${shown} room${shown === 1 ? '' : 's'}`;

  return (
    <InlineShell>
      <InlineEyebrow label={eyebrow} />
      <div className="f8c-room-list">
        {rooms.map((room, i) => (
          <RoomRow key={room.mediationId || i} room={room} onAction={onAction} />
        ))}
      </div>
    </InlineShell>
  );
}
